import { useFetchData } from "@/hooks/useFetchData";
import {
  clearVolatileBrowserCaches,
  safeGetLocalStorageItem,
  safeSetLocalStorageItem,
} from "@/libs/browserStorage";
import { CategoryProjectType } from "@/types";
import { createContext, ReactNode, useContext, useEffect } from "react";

export interface TypeProjectContextType {
  typesProject: CategoryProjectType[] | null;
}

const TypeProjectContext = createContext<TypeProjectContextType | undefined>(
  undefined
);

function readStoredTypesProject() {
  const storedTypesProject = safeGetLocalStorageItem("typesProject");
  if (!storedTypesProject) return [];

  try {
    const parsed = JSON.parse(storedTypesProject);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const TypeProjectProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { data } = useFetchData(
    `${import.meta.env.VITE_API_ENDPOINT}/project-types`
  );
  // Vérifier si 'data' est un tableau avant de le passer au contexte
  const typesProject =
    typeof data == "object" && data ? data["hydra:member"] : null;

  useEffect(() => {
    if (!typesProject) return;
    clearVolatileBrowserCaches();
    safeSetLocalStorageItem("typesProject", JSON.stringify(typesProject));
  }, [typesProject]);

  return (
    <TypeProjectContext.Provider value={{ typesProject }}>
      {children}
    </TypeProjectContext.Provider>
  );
};

// Custom hook pour utiliser le contexte
// eslint-disable-next-line react-refresh/only-export-components
export const useTypeProjectContext = (): TypeProjectContextType => {
  const context = useContext(TypeProjectContext);
  if (!context) {
    throw new Error(
      "useTypeProjectContext doit être utilisé à l'intérieur d'un TypeProjectProvider"
    );
  }

  if (!context.typesProject) {
    return {
      typesProject: readStoredTypesProject(),
    };
  }

  return context;
};

export default TypeProjectContext;
